import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { DeliveryDefenseService } from 'app/delivery-defense/services/delivery-defense.service';
import dayjs from 'dayjs';
import { firstValueFrom, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AppStateService } from '../../../app-state.service';
import { AppState } from '../../../app.state';
import { IAppState } from '../../../shared/models/app-state.interface';
import { NotificationType } from '../../../shared/models/notification-type';
import { NotificationService } from '../../../shared/services/notification/notification.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { UserRouteService } from '../user-routes/user-routes.service';

@Injectable()
export class UserRouteGuard {
    public constructor(private router: Router,
                       private authenticationService: AuthenticationService,
                       private userRouteService: UserRouteService,
                       private deliveryDefenseService: DeliveryDefenseService,
                       private readonly appState: AppState,
                       private readonly appStateService: AppStateService,
                       private readonly notificationService: NotificationService,
    ) {
    }

    public async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        const isLoggedIn = await firstValueFrom(this.authenticationService.isLoggedIn());

        if (state.url !== '/login' && !isLoggedIn) {
            return this.router.navigate(['/login']);
        }

        return firstValueFrom(
            this.refreshAppState()
                .pipe(
                    map(async (userData) => {
                        const canAccessRoute = await this.userRouteService.canAccessRoute(userData.user, userData.customer, route.data['name']);

                        if (!canAccessRoute) {
                            return this.router.navigate(['/dashboard']);
                        }

                        await this.preventAccess(state);

                        return true;
                    }),
                ),
        );
    }

    private async preventAccess(state: RouterStateSnapshot): Promise<boolean> {
        // (DD-440) Prevent Access to /score-history and /address-confidence-tool pages
        const customer = this.appState.customer$();
        let ddSubTypeID: number;

        if (customer.DDEnabled === true) {
            const ddCredit = await firstValueFrom(this.deliveryDefenseService.getRemainingDDCredit())
                .catch(async (err) => {
                    console.error(err);
                    this.notificationService.notify(
                        'Failed getting the remaining DeliveryDefense scores. Please contact administrator.',
                        'Error',
                        NotificationType.ERROR,
                    );

                    return null;
                });
            ddSubTypeID = ddCredit?.SubTypeID;
        }

        const ddPages = [
            '/score-history',
            '/address-confidence-tool',
        ];

        if (ddPages.includes(state.url)) {
            if (customer?.DDEnabled !== true || ddSubTypeID === 5) {
                return this.router.navigate(['/dashboard']);
            }
        }

        // MV3-7300: Display claims only for customers with Shipping Station ID 15
        const claimsPages = [
            '/claims',
            '/claims/dashboard',
            '/claims/file-claim',
            '/claims/message-center',
            '/claims/faq',
        ];
    }

    private refreshAppState(shouldForceRefresh = false): Observable<IAppState> {
        if (shouldForceRefresh || !this.appState.lastRetrievalDate) {
            return this.appStateService.refreshAppState();
        }

        // Try to return existing data if trying to refresh within x min.
        const xMinAfterLastRetrieval = dayjs(this.appState.lastRetrievalDate).add(environment.appStateExpirationInMinutes || 5, 'minute');
        if (dayjs().isBefore(xMinAfterLastRetrieval)) {
            const user = this.appState.user$();
            const customer = this.appState.customer$();
            const userRoutes = this.appState.userRoutes$();

            if (user && customer && userRoutes) {
                return of({ user, customer, userRoutes });
            }
        }

        return this.appStateService.refreshAppState();
    }
}
